import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, PaperPlaneTilt, CloudArrowUp, Eye } from '@phosphor-icons/react';
import api from '../services/api';
import { DODDStatsCards } from '../components/dodd/DODDStatsCards';
import { DODDClaimsTable } from '../components/dodd/DODDClaimsTable';
import { DODDClaimModal, WAIVER_TYPES } from '../components/dodd/DODDClaimModal';
import { EMBSExportPreviewDialog } from '../components/dodd/EMBSExportPreviewDialog';

const emptyForm = {
  patient_id: '',
  employee_id: '',
  service_date: new Date().toISOString().split('T')[0],
  waiver_type: '',
  service_code: '',
  time_in: '09:00',
  time_out: '11:00',
  units: 0,
  is_overtime: false,
  is_longevity: false,
  paws_authorization_number: '',
  county_bdd: '',
  notes: '',
};

const DODDBillingPage = () => {
  const [claims, setClaims] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doddCodes, setDoddCodes] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterWaiver, setFilterWaiver] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedWaiverCodes, setSelectedWaiverCodes] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterWaiver) params.waiver_type = filterWaiver;

      const [claimsRes, employeesRes, patientsRes, codesRes] = await Promise.all([
        api.get('/dodd/claims', { params }),
        api.get('/employees', { params: { status: 'Active' } }),
        api.get('/patients', { params: { payer_type: 'DODD' } }),
        api.get('/dodd/codes'),
      ]);
      setClaims(claimsRes.data);
      setEmployees(employeesRes.data);
      setPatients(patientsRes.data);
      setDoddCodes(codesRes.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterWaiver]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenModal = () => {
    setFormData(emptyForm);
    setSelectedWaiverCodes([]);
    setIsModalOpen(true);
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'waiver_type' && doddCodes[value]) {
      setSelectedWaiverCodes(doddCodes[value]);
      setFormData((prev) => ({ ...prev, service_code: '' }));
    }
    if (name === 'patient_id' && value) {
      const selectedPatient = patients.find((p) => p.id === value);
      if (selectedPatient?.county) {
        setFormData((prev) => ({ ...prev, county_bdd: selectedPatient.county }));
      }
    }
  };

  // Auto-calculate units when times change
  useEffect(() => {
    if (formData.time_in && formData.time_out) {
      const [inHours, inMins] = formData.time_in.split(':').map(Number);
      const [outHours, outMins] = formData.time_out.split(':').map(Number);
      let totalInMins = inHours * 60 + inMins;
      let totalOutMins = outHours * 60 + outMins;
      if (totalOutMins < totalInMins) totalOutMins += 24 * 60;
      const durationMins = totalOutMins - totalInMins;
      const units = Math.round((durationMins / 15) * 100) / 100;
      setFormData((prev) => ({ ...prev, units }));
    }
  }, [formData.time_in, formData.time_out]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/dodd/claims', formData);
      toast.success('DODD claim created');
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create claim');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitClaim = async (claimId) => {
    try {
      await api.post(`/dodd/claims/${claimId}/submit`);
      toast.success('Claim submitted for processing');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit claim');
    }
  };

  const handlePreviewExport = async () => {
    try {
      const response = await api.get('/integrations/embs/preview', { params: { status: 'Pending' } });
      setPreviewData(response.data);
      setShowPreview(true);
    } catch (error) {
      if (error.response?.status === 404) toast.error('No pending claims to export');
      else toast.error('Failed to preview export');
    }
  };

  const handleExportEMBS = async (format = 'flat_file') => {
    setExporting(true);
    try {
      const response = await api.get('/integrations/embs/export', {
        params: { status: 'Pending', format },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let fileName = format === 'flat_file'
        ? `embs_export_${new Date().toISOString().split('T')[0]}.txt`
        : `embs_export_${new Date().toISOString().split('T')[0]}.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) fileName = match[1];
      }
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`eMBS ${format === 'flat_file' ? 'flat file' : 'CSV'} exported successfully`);
      setShowPreview(false);
    } catch (error) {
      if (error.response?.status === 404) toast.error('No pending claims to export');
      else toast.error(error.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const codeInfo = formData.service_code && selectedWaiverCodes.length
    ? selectedWaiverCodes.find((c) => c.code === formData.service_code)
    : null;
  const estimatedReimbursement = codeInfo ? (formData.units * codeInfo.rate).toFixed(2) : '0.00';
  const pendingCount = claims.filter((c) => c.status === 'Pending').length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dodd-billing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            DODD Billing
          </h1>
          <p className="text-slate-500 mt-1">
            Ohio Department of Developmental Disabilities - eMBS Integration
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handlePreviewExport}
            disabled={pendingCount === 0}
            data-testid="preview-embs-btn"
          >
            <Eye size={18} className="mr-2" />
            Preview Export
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExportEMBS('flat_file')}
            disabled={exporting || pendingCount === 0}
            data-testid="export-embs-btn"
          >
            <CloudArrowUp size={18} className="mr-2" />
            Export eMBS File
          </Button>
          <Button
            onClick={handleOpenModal}
            className="bg-indigo-900 hover:bg-indigo-800"
            data-testid="new-dodd-claim-btn"
          >
            <Plus size={18} className="mr-2" />
            New DODD Claim
          </Button>
        </div>
      </div>

      <DODDStatsCards claims={claims} filterStatus={filterStatus} onFilterChange={setFilterStatus} />

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={filterStatus || 'all'} onValueChange={(val) => setFilterStatus(val === 'all' ? '' : val)}>
              <SelectTrigger className="w-full sm:w-40" data-testid="dodd-status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Draft">Draft</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Submitted">Submitted</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterWaiver || 'all'} onValueChange={(val) => setFilterWaiver(val === 'all' ? '' : val)}>
              <SelectTrigger className="w-full sm:w-56" data-testid="dodd-waiver-filter">
                <SelectValue placeholder="All Waivers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Waivers</SelectItem>
                {WAIVER_TYPES.map((w) => (
                  <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <DODDClaimsTable
        claims={claims}
        loading={loading}
        onSubmitClaim={handleSubmitClaim}
        onCreateNew={handleOpenModal}
      />

      <DODDClaimModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        formData={formData}
        onChange={handleChange}
        onSubmit={handleSubmit}
        saving={saving}
        selectedWaiverCodes={selectedWaiverCodes}
        patients={patients}
        employees={employees}
        codeInfo={codeInfo}
        estimatedReimbursement={estimatedReimbursement}
      />

      <EMBSExportPreviewDialog
        open={showPreview}
        onOpenChange={setShowPreview}
        previewData={previewData}
        onExport={handleExportEMBS}
        exporting={exporting}
      />
    </div>
  );
};

export default DODDBillingPage;
